import { push, goBack } from 'connected-react-router';

// Design System
import transformUkPhoneNumber from 'jl-design-system/form/normalizers/transformUkPhoneNumber';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import stripWhitespace from 'jl-design-system/utils/string/stripWhitespace';

// Config
import {
  ANALYTICS_COLLECTION_POINT_DOWN,
  ANALYTICS_COLLECTION_POINT_CUT_OFF_MODAL_SHOWN,
  ANALYTICS_COLLECTION_POINT_SUCCESS,
  ANALYTICS_FIND_COLLECTION_POINT_FAILED,
  SET_COLLECTION_POINT_VIEW_TYPE,
  CLEAR_COLLECTION_POINT_SELECTION,
  FIND_COLLECTION_POINT,
  GET_COLLECTION_POINT_DETAILS,
  SELECT_COLLECTION_POINT,
  SET_FIND_COLLECTION_POINT_ERROR,
  NEW_COLLECTION_POINT_SEARCH,
  PUT_CC_DELIVERY_DETAILS,
  PUT_CC_DELIVERY_DETAILS_SILENTLY,
  SET_ACTIVE_MAP_MARKER,
  SET_ACTIVE_MODAL_MAP_MARKER,
  GET_COLLECTION_POINT_DATES,
  SET_CLICK_COLLECT_COUNTDOWN_VISIBILITY,
  SET_CLICK_COLLECT_CUT_OFF_PASSED,
  SET_CLICK_COLLECT_CUT_OFF_PASSED_MODAL_VISIBILITY,
  DELETE_COLLECTION_POINT,
  SHOW_REMOVE_COLLECTION_POINT_OVERLAY,
  HIDE_REMOVE_COLLECTION_POINT_OVERLAY,
  GET_SAVED_COLLECTION_POINTS,
  SET_GEO_LOCATION_SEARCH_ACTIVE_STATUS,
  SILENTLY_SELECT_COLLECTION_DATE,
  GOOGLE_MAPS_SCRIPT_LOADED,
  GOOGLE_MAPS_SCRIPT_LOADING,
  CHANGE_COLLECTION_POINT,
} from '@constants/actionConstants';
import { VIEW_TYPE_MAP, STORE_DETAILS, SEARCH_TERMS_TO_MODIFY } from '@constants/clickCollectConstants';
import {
  URL_COLLECTION_POINTS,
  URL_COLLECTION_POINT_BY_ID,
  URL_COLLECTION_DETAILS,
  URL_CUSTOMER_COLLECTION_POINTS,
  URL_COLLECTION_DATES,
  URL_DELETE_COLLECTION_POINT,
  URL_ORDERFORM_COLLECTION_DATES,
} from '@constants/endpointConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import featureConstants from '@constants/featureConstants';
import routeConstants from '@constants/routeConstants';
import {
  getUnavailableStores,
  getUnavailableStoresAnalyticsInfo,
  selectClickAndCollectStoreAnalytics,
} from '@utils/analytics/utils/getCollectionAnalytics';
import { isCollectPlusRelatedType } from '@utils/collection/getCollectPlusRelatedStores';
import triggerAnalyticsEvent from '@redux/actions/analytics/analyticsAction';
import { recordImpressions } from '@redux/actions/app/appActions';
import { initPaymentPage } from '@redux/actions/payment/orderFormActions';
import { initDeliveryPage } from '@redux/actions/delivery/deliveryActions';
import { recordCollectionDateTimings, saveTimeContinueToPaymentClicked } from '@redux/actions/timing/timingActions';
import getGiftMessageLines from '@utils/delivery/getGiftMessageLines';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';
import daysBetweenDateAndStartOfWeek from '@utils/helpers/date/daysBetweenDateAndStartOfWeek';
import sortCollectionPointsByDistance from '@utils/collection/sortCollectionPointsByDistance';
import { COLLECTOR_DETAILS_FORM_ID } from '@utils/form/configs/collectorDetails';
import isCollectionPointAvailable from '@utils/collection/isCollectionPointAvailable';
import { isEmptyObject } from '@utils/object';
import { isFeatureActive } from '@redux/reducers/config/configReducer';

export const recordProminentUseLocationFeatureImpressionId = () => async (dispatch, getState) => {
  const { config: { features = [] } = {} } = getState() ?? {};

  const featureFlag = features?.find((feature) => feature.id === featureConstants.PROMINENT_USE_LOCATION) ?? {};
  const impressionId = featureFlag.impressionId;

  if (impressionId) {
    return dispatch(recordImpressions(impressionId));
  }
};

export function getClickCollectCollectionPoints(unfilteredCollectionPoints = []) {
  const filteredCollectionPoints = unfilteredCollectionPoints.filter(
    point => !isCollectPlusRelatedType(point.ownerId),
  );

  return sortCollectionPointsByDistance(filteredCollectionPoints);
}

export function getCollectPlusRelatedCollectionPoints(unfilteredCollectionPoints = []) {
  const filteredCollectionPoints = unfilteredCollectionPoints.filter(
    point => isCollectPlusRelatedType(point.ownerId),
  );

  return sortCollectionPointsByDistance(filteredCollectionPoints);
}

export const determinePreSelectedCollectionPoint = (userCollectionPoints) => {
  if (userCollectionPoints.length > 0) {
    const userSelectedDefaultCollectionPoint = userCollectionPoints.find(point => point.isDefault || point.default);

    if (userSelectedDefaultCollectionPoint) {
      return userSelectedDefaultCollectionPoint;
    }

    if (userCollectionPoints[0]) {
      return userCollectionPoints[0];
    }
  }

  return undefined;
};

export const submitCCDeliveryDetails = body => (dispatch, getState) => {

  // analytics details
  let detailsToKeep = {
    clickandCollectType: 'standard',
  };

  const collectionPoint = getState()?.clickAndCollect?.selectedCollectionPoint ?? {};
  const selectedCollectionTime = getState()?.clickAndCollect?.selectedCollectionTime;
  const collectionPointDates = getState()?.clickAndCollect?.collectionPointDates;

  if (selectedCollectionTime && collectionPointDates) {
    const firstDateInCollectionPointDates = collectionPointDates?.[0]?.date;
    const firstAvailableDay = daysBetweenDateAndStartOfWeek(firstDateInCollectionPointDates);
    const selectedDaysUntilDelivery = daysBetweenDateAndStartOfWeek(selectedCollectionTime);

    detailsToKeep = {
      firstAvailableDay,
      clickandCollectType: 'named day',
      selectedDaysUntilDelivery,
      deliveryMessage: 'Click and Collect',
    };
  }

  return dispatch({
    type: PUT_CC_DELIVERY_DETAILS,
    request: client => client({ path: URL_COLLECTION_DETAILS, config: { method: 'PUT', body } }),
    collectionPoint,
    body,
    ...detailsToKeep,
  });
};

export const getCollectionPointDates = (collectionPointId) => async (dispatch, getState) => {
  const batchId = isFeatureActive(getState(), featureConstants.APO_ENABLED)
    ? getState()?.clickAndCollect?.batchId
    : getState()?.delivery?.deliveries?.[0]?.id;

  const body = {
    batchId,
    collectionPointId: collectionPointId || getState()?.clickAndCollect?.selectedCollectionPoint?.id,
  };

  const path = isFeatureActive(getState(), featureConstants.APO_ENABLED)
    ? URL_ORDERFORM_COLLECTION_DATES
    : URL_COLLECTION_DATES;

  return dispatch({
    type: GET_COLLECTION_POINT_DATES,
    request: client => client({ path, config: { method: 'POST', body } }),
    selectingCollectionPointId: body.collectionPointId,
  });
};

export const nextDayCollectionDateExists = (collectionPointDates, selectedCollectionDate) => {
  const selectedDateIndex = collectionPointDates.findIndex(
    date => date.fulfilmentOfferId === selectedCollectionDate.fulfilmentOfferId,
  );
  const nextDateExists = !!collectionPointDates[selectedDateIndex + 1];

  return nextDateExists;
};

export const silentlySelectCollectionDate = payload => ({
  type: SILENTLY_SELECT_COLLECTION_DATE,
  payload,
});

export const getAndSelectNewCollectionDate = () => async (dispatch) => {
  const dateResponse = await dispatch(getCollectionPointDates());
  const date = dateResponse?.result?.collectionDates?.find(date => date.isAvailable)?.date;

  if (date) return dispatch(silentlySelectCollectionDate(date));

  return {};
};

export const setCutOffPassedModalVisibility = visible => async (dispatch, getState) => {
  const selectedCollectionDate = getState()?.clickAndCollect?.selectedCollectionDate ?? {};
  const collectionPointDates = getState()?.clickAndCollect?.collectionPointDates ?? [];

  if (visible) {
    dispatch(triggerAnalyticsEvent(ANALYTICS_COLLECTION_POINT_CUT_OFF_MODAL_SHOWN));

    // check that we have another date to suggest and get new date if not
    if (!nextDayCollectionDateExists(collectionPointDates, selectedCollectionDate)) {
      await dispatch(getAndSelectNewCollectionDate());
    }
  }

  dispatch({
    type: SET_CLICK_COLLECT_CUT_OFF_PASSED_MODAL_VISIBILITY,
    visible,
  });
};

export const submitClickAndCollectDeliveryDetails = ({
  formValues = {},
  shouldRecordDeliveryTimings = false,
}) => async (dispatch, getState) => (
  new Promise(async (resolve) => {
    const state = getState();
    const {
      countdownVisible = false,
      cutOffPassed = false,
      showCutOffPassedModal = false,
    } = state.clickAndCollect ?? {};

    if (cutOffPassed && countdownVisible && !showCutOffPassedModal) {
      dispatch(setCutOffPassedModalVisibility(true));
      resolve();
    } else {
      dispatch(saveTimeContinueToPaymentClicked());
      const {
        collectionPointDates,
        selectedCollectionDate = {},
        suggestedNewCollectionDate,
      } = state.clickAndCollect ?? {};

      const { ...rest } = formValues;
      const giftMessage = state.app?.defaultGiftMessageValue?.giftReceipt ?? '';
      const giftReceipt = state.app?.isGiftReceiptSelected?.giftReceipt ?? false;

      const collectorDetails = {
        ...rest,
        giftMessageLines: getGiftMessageLines({ giftMessage, giftReceipt }),
      };

      const fulfilmentOfferId = cutOffPassed ?
        suggestedNewCollectionDate?.fulfilmentOfferId :
        selectedCollectionDate?.fulfilmentOfferId || collectionPointDates?.[0]?.fulfilmentOfferId;

      const body = {
        collectorDetails: {
          primaryPhone: stripWhitespace(transformUkPhoneNumber(collectorDetails.phoneNumber || '')),
        },
        fulfilment: {
          fulfilmentOfferId,
          giftMessageLines: collectorDetails.giftMessageLines,
        },
      };

      const response = await dispatch(submitCCDeliveryDetails(body));

      if (response.type === `${PUT_CC_DELIVERY_DETAILS}.SUCCESS`) {
        dispatch(initPaymentPage({
          pushAfter: true,
          shouldRecordDeliveryTimings,
        }));
      } else if (response.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE) {
        await dispatch(handleInconsistentOrderFormState());
      } else {
        dispatch(initDeliveryPage());
        dispatch(push(routeConstants.DELIVERY));
      }

      resolve();
    }
  })
);

export const getCollectionPointById = (collectionPointSearchTerm, refresh = false) => async (dispatch) => {
  let searchTermForAPI = collectionPointSearchTerm;
  if (SEARCH_TERMS_TO_MODIFY.includes(collectionPointSearchTerm.toLowerCase())) {
    searchTermForAPI = encodeURI(`${collectionPointSearchTerm} UK`);
  }

  return dispatch({
    type: FIND_COLLECTION_POINT,
    collectionPointSearchTerm,
    request: client => client({
      path: URL_COLLECTION_POINTS(searchTermForAPI),
      config: { method: 'GET' },
    }),
    refresh,
  });
};

export const findCollectionPoint = (collectionPointSearchTerm, refresh = false) => async (dispatch, getState) => {
  const response = await dispatch(getCollectionPointById(collectionPointSearchTerm, refresh));

  if (response.type === `${FIND_COLLECTION_POINT}.FAILED`) {
    dispatch(triggerAnalyticsEvent(ANALYTICS_COLLECTION_POINT_DOWN));
  }

  if (response.type === `${FIND_COLLECTION_POINT}.SUCCESS`) {
    const collectionPointsFound = (response?.result?.collectionPoints ?? []).length;
    if (!collectionPointsFound) {
      dispatch(triggerAnalyticsEvent(ANALYTICS_FIND_COLLECTION_POINT_FAILED));
      return;
    }

    const searchNewCollectPoint = getState().clickAndCollect.collectionPointSearchTerm;
    const collectionPointSearchResults = getState().clickAndCollect.collectionPointSearchResults;
    const clickCollectCollectionPoints = getClickCollectCollectionPoints(collectionPointSearchResults);
    const collectPlusCollectionPoints = getCollectPlusRelatedCollectionPoints(collectionPointSearchResults);
    const unavailableStores = getUnavailableStores(collectionPointSearchResults);

    const deliveryProposition = {
      method: 'col',
      selected: 1,
      deliveryId: '',
      message: '',
      searchTerm: searchNewCollectPoint,
      ccShopsShown: clickCollectCollectionPoints.length,
      cpShopsShown: collectPlusCollectionPoints.length,
    };

    deliveryProposition.countdownVisible = !!getState().clickAndCollect?.countdownVisible;

    dispatch(triggerAnalyticsEvent(ANALYTICS_COLLECTION_POINT_SUCCESS, {
      analytics: {
        checkout: {
          batch: [{
            deliveryProposition: [deliveryProposition],
          }],
        },
        error: unavailableStores.map(item => ({
          type: 'checkout:info',
          message: getUnavailableStoresAnalyticsInfo(item),
        })),
      },
    }));
  }
};

export const newCollectionPointSearch = () => ({
  type: NEW_COLLECTION_POINT_SEARCH,
});

// used when C&C store is selected and partner discount is toggled on/off
// used when edit basket could have caused a change in collection point charges
export const getCollectionPointDetails = collectionPointId => ({
  type: GET_COLLECTION_POINT_DETAILS,
  request: client => client({ path: URL_COLLECTION_POINT_BY_ID(collectionPointId), config: { method: 'GET' } }),
});

export const getSavedCollectionPoints = () => ({
  type: GET_SAVED_COLLECTION_POINTS,
  request: client => client({ path: URL_CUSTOMER_COLLECTION_POINTS, config: { method: 'GET' } }),
});

// TODO rewrite this function to be simpler
// https://www.jlpit.com/jira/browse/MARV-10645
export const selectCollectionPoint = ({
  collectionPoint: _collectionPoint,
  refresh = false,
  selectedIndex = 0,
  shouldRecordDeliveryTimings = false,
  datesAlreadyLoaded = false,
}) => async (dispatch, getState) => {

  let collectionPoint = { ..._collectionPoint };

  if (refresh) {
    const getDetails = await dispatch(getCollectionPointDetails(collectionPoint.id));

    if (getDetails.type === `${GET_COLLECTION_POINT_DETAILS}.SUCCESS`) {
      collectionPoint = getDetails.result.collectionPoints[0];
    }

    if (getDetails.type === `${GET_COLLECTION_POINT_DETAILS}.FAILED`) {
      dispatch(push(routeConstants.DELIVERY));
      return;
    }
  }

  const deliveryOptions = getState()?.delivery?.deliveryOptions;
  const isAvailable = isCollectionPointAvailable(collectionPoint, deliveryOptions);

  if (isAvailable && !datesAlreadyLoaded) {
    await dispatch(getCollectionPointDates(collectionPoint.id));
  }

  const searchTerm = getState()?.clickAndCollect?.collectionPointSearchTerm;
  const collectionDates = getState()?.clickAndCollect?.collectionPointDates ?? [];
  const analytics = selectClickAndCollectStoreAnalytics(
    collectionPoint,
    searchTerm,
    collectionDates,
    selectedIndex,
  );

  await dispatch({
    type: SELECT_COLLECTION_POINT,
    collectionPoint,
    analytics,
  });

  const firstAvailableDate = collectionDates.find(date => date.isAvailable);

  if (firstAvailableDate) {
    // Collect+ stores don't show a calendar but we get dates anyway for fulfilmentOfferId reasons
    // then 'select' the first available date for the customer
    dispatch(silentlySelectCollectionDate(firstAvailableDate.date));
  }

  if (refresh) {
    return;
  }

  if (!getState().router?.location?.pathname.includes(routeConstants.CLICK_AND_COLLECT_STORE_SELECTED)) {
    dispatch(push(routeConstants.CLICK_AND_COLLECT_STORE_SELECTED));
  }

  if (shouldRecordDeliveryTimings) {
    dispatch(recordCollectionDateTimings());
  }
};

export const clearCollectionPointSelection = () => ({
  type: CLEAR_COLLECTION_POINT_SELECTION,
});

export const setCollectionPointViewType = activeViewType => async (dispatch) => {
  const url = activeViewType === VIEW_TYPE_MAP ?
    routeConstants.CLICK_AND_COLLECT_SEARCH_MAP : routeConstants.CLICK_AND_COLLECT_SEARCH_LIST;

  await dispatch(push(url));

  dispatch({
    type: SET_COLLECTION_POINT_VIEW_TYPE,
    activeViewType,
  });
};

export const closeMoreDetailsModal = () => (dispatch) => {
  dispatch(goBack());
};

export const openMoreDetailsModal = ({ id = '', route }) => (dispatch) => {
  const path = `${route}/${STORE_DETAILS}`;
  dispatch(push(path, { id }));
};

export const setActiveMapMarker = marker => ({
  type: SET_ACTIVE_MAP_MARKER,
  marker,
});

export const setActiveModalMapMarker = marker => ({
  type: SET_ACTIVE_MODAL_MAP_MARKER,
  marker,
});

export const setFindCollectionPointError = error => ({
  type: SET_FIND_COLLECTION_POINT_ERROR,
  error,
});

export const openSavedCollectionPointsModal = () => async (dispatch) => {
  dispatch(push(routeConstants.CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS));
};

export const changeClickAndCollectSelectedPoint = () => async (dispatch, getState) => {
  dispatch(triggerAnalyticsEvent(CHANGE_COLLECTION_POINT));

  const savedCollectionPoints = getState()?.user?.collectionPoints ?? [];

  if (savedCollectionPoints.length > 1) {
    dispatch(openSavedCollectionPointsModal());
    return;
  }

  const url = getState()?.clickAndCollect?.activeViewType === VIEW_TYPE_MAP ?
    routeConstants.CLICK_AND_COLLECT_SEARCH_MAP : routeConstants.CLICK_AND_COLLECT_SEARCH_LIST;

  dispatch(push(url));
  dispatch(newCollectionPointSearch());
};

export const initSavedCollectionPoint = ({
  refresh,
  datesAlreadyLoaded,
} = {}) => (dispatch, getState) => {
  const state = getState();

  // TODO: What is relevance of collectionPointSearchComplete here?
  const collectionPointSearchComplete = state?.clickAndCollect?.collectionPointSearchComplete ?? false;
  const collectionPoint = determinePreSelectedCollectionPoint(state?.user?.collectionPoints ?? []);

  if (collectionPoint && !collectionPointSearchComplete) {
    return dispatch(selectCollectionPoint({
      collectionPoint,
      shouldRecordDeliveryTimings: true,
      datesAlreadyLoaded,
    }));
  }

  // refresh means customer encountered inconsistent order form error so we need to refresh selected
  // collection point dates to get up to date fulfilmentOfferIds
  if (refresh && !isEmptyObject(state.clickAndCollect.selectedCollectionPoint)) {
    return dispatch(selectCollectionPoint({ collectionPoint: state.clickAndCollect.selectedCollectionPoint, refresh }));
  }

  return null;
};

export const setClickCollectCountdownVisibility = visible => ({
  type: SET_CLICK_COLLECT_COUNTDOWN_VISIBILITY,
  visible,
});

export const setClickCollectCutOffPassed = cutOffPassed => ({
  type: SET_CLICK_COLLECT_CUT_OFF_PASSED,
  cutOffPassed,
});

export const handleCutOffPassedModalChangeDateClick = params => async (dispatch) => {
  const {
    isPayment,
    modalVisible = true,
  } = params;

  if (isPayment) {
    await dispatch(push(routeConstants.CLICK_AND_COLLECT_STORE_SELECTED));
  } else {
    dispatch(resetForm(COLLECTOR_DETAILS_FORM_ID));
  }

  if (modalVisible) {
    dispatch(setCutOffPassedModalVisibility(false));
  }

  dispatch(getCollectionPointDates());
};

export const deleteCollectionPoint = collectionPointId => ({
  type: DELETE_COLLECTION_POINT,
  request: client => client({
    path: URL_DELETE_COLLECTION_POINT(collectionPointId),
    config: {
      method: 'DELETE',
    },
  }),
});

export const showDeleteCollectionPointOverlay = id => ({
  type: SHOW_REMOVE_COLLECTION_POINT_OVERLAY,
  id,
});

export const hideDeleteCollectionPointOverlay = () => ({
  type: HIDE_REMOVE_COLLECTION_POINT_OVERLAY,
});

export const setGeoLocationSearchActiveStatus = active => ({
  type: SET_GEO_LOCATION_SEARCH_ACTIVE_STATUS,
  active,
});

export const onGoogleMapsScriptLoadSuccess = () => ({
  type: GOOGLE_MAPS_SCRIPT_LOADED,
});

export const onGoogleMapsScriptLoading = () => ({
  type: GOOGLE_MAPS_SCRIPT_LOADING,
});

export const collectionCutOffPassedDuringSubmitOrder = () => async (dispatch, getState) => {
  const ownerId = getState().clickAndCollect.selectedCollectionPoint?.ownerId;
  const isCollectPlusRelatedOwners = isCollectPlusRelatedType(ownerId);

  if (isCollectPlusRelatedOwners) {
    // we only get one date for Collect+ so we need to get a new date
    await dispatch(getAndSelectNewCollectionDate());
  }

  const state = getState();

  const availableDates = (state?.clickAndCollect?.collectionPointDates ?? []).filter(({ isAvailable }) => isAvailable);
  const targetDate = isCollectPlusRelatedOwners ? availableDates[0] : availableDates[1];

  if (!targetDate) {
    // no more available dates so fake an error response to avoid a pointless API call
    return dispatch({
      type: `${PUT_CC_DELIVERY_DETAILS_SILENTLY}.FAILED`,
    });
  }

  const fulfilmentOfferId = targetDate.fulfilmentOfferId;
  const collectorDetails = state.clickAndCollect.collectorDetails;
  const giftMessageLines = state.clickAndCollect.giftMessageLines;

  const body = {
    collectorDetails,
    fulfilment: {
      fulfilmentOfferId,
      giftMessageLines,
    },
  };

  return dispatch({
    type: PUT_CC_DELIVERY_DETAILS_SILENTLY,
    request: client => client({ path: URL_COLLECTION_DETAILS, config: { method: 'PUT', body } }),
  });
};
